'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSignUp } from '@clerk/nextjs';
import { createClerkFinalizeNavigate } from '@lib/clerk/utils/create-clerk-finalize-navigate';
import { isClerkIdentifierExistsError } from '@lib/clerk/utils/is-clerk-identifier-exists-error';
import { resolveClerkErrorMessage } from '@lib/clerk/utils/resolve-clerk-error-message';
import { useQueryClient } from '@lib/query/client';
export interface SignUpCredentials {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}
type SignUpWithCredentialsOutcome = 'complete' | 'identifier_exists' | 'failed';
export interface UseClerkSignUpResult {
  signUpWithCredentials: (credentials: SignUpCredentials, redirectTo: string) => Promise<SignUpWithCredentialsOutcome>;
  isLoading: boolean;
  error: string | null;
}
export function useClerkSignUp(): UseClerkSignUpResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    signUp
  } = useSignUp();
  const signUpWithCredentials = async (credentials: SignUpCredentials, redirectToPath: string): Promise<SignUpWithCredentialsOutcome> => {
    setIsLoading(true);
    setError(null);
    try {
      const {
        error: signUpError
      } = await signUp.password({
        emailAddress: credentials.email,
        password: credentials.password,
        firstName: credentials.firstName,
        lastName: credentials.lastName
      });
      if (signUpError) {
        if (isClerkIdentifierExistsError(signUpError)) {
          return 'identifier_exists';
        }
        setError(resolveClerkErrorMessage(signUpError, {
          flow: 'sign-up',
          userInfo: {
            email: credentials.email,
            firstName: credentials.firstName,
            lastName: credentials.lastName
          },
          fallback: 'An error occurred during sign up'
        }));
        return 'failed';
      }
      if (signUp.status === 'complete') {
        // Navigation must happen inside finalize: a signed-in
        // user cannot remain on /signup (see the callback).
        const {
          error: finalizeError
        } = await signUp.finalize({
          navigate: createClerkFinalizeNavigate(router, redirectToPath)
        });
        if (finalizeError) {
          setError(finalizeError.message);
          return 'failed';
        }
        queryClient.clear();
        return 'complete';
      }
      setError('Sign up incomplete. Please try again.');
      return 'failed';
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred during sign up');
      return 'failed';
    } finally {
      setIsLoading(false);
    }
  };
  return {
    signUpWithCredentials,
    isLoading,
    error
  };
}